import { Injectable } from '@angular/core'
import { EMPTY, Observable, catchError, map, of } from 'rxjs'
import { Login } from 'src/app/models/model'
import { WebService } from '../api/web.service'
import { HttpHeaders } from '@angular/common/http'
import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'
import { AuthService } from '../common/auth.service'
import { AppState } from 'src/app/store/state/app.state'
import { Store } from '@ngrx/store'
import { start_forgot_password, start_reset_password_with_password } from 'src/app/store/action/login.action'
import { CommonService } from '../common/common.service'
import { HttpErrorResponseModel, LoginDiscoveryResponseApiModel } from 'src/app/models/api.model'

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  constructor(private webService: WebService, private appState: Store<AppState>, private cs: CommonService) {}

  login(email: string, password: string): Observable<Login> {
    const formData = new FormData()
    formData.append('email', email)
    formData.append('password', password)

    return this.webService
      .postWithoutAuth(ServiceUrlConstants.API_LOGIN, formData, null, [], true)
      .pipe(map((data: Login) => data))
  }

  refreshToken = () => {
    let headers = new HttpHeaders()
    return this.webService.getWithRefresh(ServiceUrlConstants.API_SESSION_REFRESH, null, headers)
  }

  addRefreshHeader = (headers: HttpHeaders) => {
    if (!headers) {
      headers = new HttpHeaders()
    }
    const token = AuthService.getRefreshToken()
    headers = headers.set('refreshToken', `${token}`)
    return headers
  }

  logout = () => {
    const headers = new HttpHeaders()
    return this.webService.postWithAuthTokenAndRefresh(ServiceUrlConstants.API_LOGOUT, null, headers)
  }

  dispatchForgotPasswordAction(email: string, callback: Function) {
    return this.appState.dispatch(start_forgot_password({ email, callback }))
  }

  callForgotPasswordAPI(email: string) {
    return this.webService.postWithoutAuth(ServiceUrlConstants.API_FORGOT_PASSWORD, email, null, [], false, true)
  }

  dispatchResetPasswordWithOldPasswordAction(oldPassword: string, newPassword: string, callback: Function) {
    return this.appState.dispatch(start_reset_password_with_password({ oldPassword, newPassword, callback }))
  }

  callResetPasswordWithOldPasswordAPI(oldPassword: string, newPassword: string) {
    return this.webService.post(ServiceUrlConstants.API_RESET_WITH_PASSWORD, { oldPassword, newPassword }, null, [])
  }

  ssoLogin(id_token: string, companyDomain: string): Observable<Login> {
    return this.webService
      .postWithoutAuth(ServiceUrlConstants.API_SSO_LOGIN, { msalToken: id_token, companyDomain }, null, [], false, false)
      .pipe(map((data: Login) => data))
  }

  sendLoginOTP(email: string): Observable<void> {
    return this.webService.postWithoutAuth(ServiceUrlConstants.API_GENERATE_OTP_FOR_LOGIN, { email }).pipe(
      map(() => void 0),
      catchError((err: HttpErrorResponseModel) => {
        const message = this.cs.extractApiErrorMessage(err) ?? 'Something went wrong. Please try again later.'

        this.cs.showError(message)
        return EMPTY
      })
    )
  }

  loginUsingOTP(email: string, otp: string) {
    const formData = new FormData()
    formData.append('email', email)
    formData.append('otp', otp)

    return this.webService
      .postWithoutAuth(ServiceUrlConstants.API_OTP_BASED_LOGIN, formData, null, [], true)
      .pipe(map((data: Login) => data))
  }

  discoverLoginOptions(companyDomain: string): Observable<LoginDiscoveryResponseApiModel> {
    return this.webService.postWithoutAuth(ServiceUrlConstants.API_AUTH_LOGIN_FOR_DISCOVERY, { companyDomain }).pipe(
      catchError((err: HttpErrorResponseModel) => {
        const message = this.cs.extractApiErrorMessage(err) ?? 'Something went wrong. Please try again later.'

        this.cs.showError(message)
        return EMPTY
      })
    )
  }
}
